import Ajv2020 from 'ajv/dist/2020';
import cloneDeep from 'lodash-es/cloneDeep';
import isPlainObject from 'lodash-es/isPlainObject';
import i18n from 'i18n';
import translations from 'i18n/translations';

export const SCHEMA_TYPE = {
  OBJECT: 'object',
  ARRAY: 'array',
  STRING: 'string',
  INTEGER: 'integer',
  NUMBER: 'number',
  BOOLEAN: 'boolean'
};

const PRIMITIVE_TYPES = [SCHEMA_TYPE.STRING, SCHEMA_TYPE.INTEGER, SCHEMA_TYPE.NUMBER, SCHEMA_TYPE.BOOLEAN];

// The form renders only plain JSON Schema; anything that combines or references schemas falls back to a JSON editor
const UNSUPPORTED_KEYWORDS = ['$ref', 'oneOf', 'anyOf', 'allOf', 'not', 'if', 'patternProperties'];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// "[A-Z]{2}" or "\\d{4,6}" bounds the length of a value; other patterns leave it open
const BOUNDED_PATTERN = /^\^?(?:\[(?:\\.|[^\\\]])+\]|\\[dw])\{(?:\d+,)?(\d+)\}\$?$/;

// Keywords holding values rather than schemas: a "pattern" key inside them is data
const VALUE_KEYWORDS = ['default', 'enum', 'const', 'examples'];

// ajv knows no formats by itself; formats not registered here are ignored with a console warning
const ajv = new Ajv2020({ allErrors: true, strict: false });
ajv.addFormat('uuid', UUID_PATTERN);
const validatorsCache = new WeakMap();

// "type" may list "null" next to the actual type
export const getSchemaType = (schema) => {
  if (!isPlainObject(schema)) return null;

  const type = Array.isArray(schema.type) ? schema.type.find((item) => item !== 'null') : schema.type;
  if (type) return type;
  if (schema.enum) return SCHEMA_TYPE.STRING;
  if (schema.properties || schema.additionalProperties) return SCHEMA_TYPE.OBJECT;
  if (schema.items) return SCHEMA_TYPE.ARRAY;

  return null;
};

// An object without fixed properties is a map: keys are free text or, with "propertyNames.enum", a fixed list.
// The backend leaves out "additionalProperties" for a Java Map, so the values of such a map are edited as JSON
export const isMapSchema = (schema) =>
  getSchemaType(schema) === SCHEMA_TYPE.OBJECT && !schema.properties && schema.additionalProperties !== false;

export const isEnumArraySchema = (schema) =>
  getSchemaType(schema) === SCHEMA_TYPE.ARRAY && Array.isArray(schema.items?.enum);

export const isStringListSchema = (schema) =>
  getSchemaType(schema) === SCHEMA_TYPE.ARRAY &&
  getSchemaType(schema.items) === SCHEMA_TYPE.STRING &&
  !schema.items.enum &&
  isSupportedSchema(schema.items);

// Checks only this level: nested parts the form cannot render fall back to the JSON editor one by one
export const isSupportedSchema = (schema) => {
  if (!isPlainObject(schema) || UNSUPPORTED_KEYWORDS.some((keyword) => keyword in schema)) return false;

  const type = getSchemaType(schema);
  if (type === SCHEMA_TYPE.ARRAY) {
    return isPlainObject(schema.items);
  }
  return type === SCHEMA_TYPE.OBJECT || PRIMITIVE_TYPES.includes(type);
};

// The backend checks a pattern against the whole value (Java @Pattern), JSON Schema finds it anywhere in the value
export const toFullMatchPattern = (pattern) => `^(?:${pattern})$`;

const withFullMatchPatterns = (schema) => {
  if (Array.isArray(schema)) return schema.map(withFullMatchPatterns);
  if (!isPlainObject(schema)) return schema;

  return Object.fromEntries(
    Object.entries(schema).map(([key, value]) => {
      if (VALUE_KEYWORDS.includes(key)) return [key, value];
      if (key === 'pattern' && typeof value === 'string') return [key, toFullMatchPattern(value)];
      return [key, withFullMatchPatterns(value)];
    })
  );
};

export const getMaxLength = (schema) => {
  if (schema?.maxLength !== undefined) return schema.maxLength;

  const match = BOUNDED_PATTERN.exec(schema?.pattern || '');
  return match ? Number(match[1]) : undefined;
};

// Value for a newly added item: required structure is filled in, choices are left for the operator
export const getEmptyValue = (schema) => {
  if (schema?.default !== undefined) return cloneDeep(schema.default);

  const type = getSchemaType(schema);
  if (type === SCHEMA_TYPE.OBJECT) {
    if (isMapSchema(schema)) return {};
    return Object.entries(schema.properties || {}).reduce((result, [key, propertySchema]) => {
      const emptyValue = getEmptyValue(propertySchema);
      if (emptyValue !== undefined) {
        result[key] = emptyValue;
      }
      return result;
    }, {});
  }
  if (type === SCHEMA_TYPE.ARRAY) return [];
  if (type === SCHEMA_TYPE.BOOLEAN) return false;
  if (type === SCHEMA_TYPE.STRING && !schema.enum) return '';
  return undefined;
};

// Paths use the backend notation for validation errors: "levels[0].levelName"
export const joinPath = (basePath, key) => {
  if (typeof key === 'number') return `${basePath}[${key}]`;
  return basePath ? `${basePath}.${key}` : key;
};

const instancePathToPath = (instancePath) =>
  instancePath
    .split('/')
    .slice(1)
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'))
    .reduce((path, segment) => joinPath(path, /^\d+$/.test(segment) ? Number(segment) : segment), '');

// ajv keywords with an own message; the rest keep the ajv text
const VALIDATION_MESSAGE_KEYS = {
  required: 'integrationSettings.form.error.required',
  enum: 'integrationSettings.form.error.enum',
  type: 'integrationSettings.form.error.type'
};

const getValidationMessage = (error) => {
  if (error.keyword === 'format') {
    return getTranslation(`integrationSettings.form.error.format.${error.params.format}`) || error.message;
  }
  // minLength 1 is how the backend marks a mandatory text (@NotBlank)
  const isEmptyText = error.keyword === 'minLength' && error.params.limit === 1;
  const messageKey = isEmptyText ? VALIDATION_MESSAGE_KEYS.required : VALIDATION_MESSAGE_KEYS[error.keyword];
  return messageKey ? i18n.getMessage(messageKey) : error.message;
};

// Returns { [path]: message } with the first problem per field; the backend runs the same checks and more
export const validateBySchema = (schema, value) => {
  if (!isPlainObject(schema)) return {};

  let validate = validatorsCache.get(schema);
  if (!validate) {
    try {
      validate = ajv.compile(withFullMatchPatterns(schema));
    } catch (e) {
      // A schema ajv cannot compile is still validated by the backend on save
      validate = () => true;
    }
    validatorsCache.set(schema, validate);
  }

  if (validate(value)) return {};

  return (validate.errors || []).reduce((result, error) => {
    const basePath = instancePathToPath(error.instancePath);
    const path = error.keyword === 'required' ? joinPath(basePath, error.params.missingProperty) : basePath;
    if (!(path in result)) {
      result[path] = getValidationMessage(error);
    }
    return result;
  }, {});
};

// "levelName" -> "Level name", "max_attempts" -> "Max attempts"
export const humanize = (text) => {
  const words = String(text)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const getTranslation = (messageKey) => (translations.en?.[messageKey] ? i18n.getMessage(messageKey) : null);

export const getFieldLabel = (schema, name) =>
  schema?.title || getTranslation(`integrationSettings.form.field.${name}`) || humanize(name);

// Enum values are often codes (GB, SEPA), so they are shown as is unless a translation exists
export const getEnumLabel = (value) => getTranslation(`integrationSettings.form.enum.${value}`) || String(value);
